import { useMemo } from 'react'
import { MUTED } from '../../shared/clubs'
import { REGION_ORDER, type Club, type Region } from '../../shared/types'
import { useI18n } from '../lib/i18n'

interface Props {
  clubs: Club[]
  selected: Set<string>
  onToggle: (clubId: string) => void
  onSet: (ids: string[]) => void
  counts: Map<string, number>
}

/**
 * The map's twin: the same include/exclude filter, but legible and tappable.
 * Grouped by region, because "anywhere in Funchal" and "I'll drive west" are
 * the two ways people actually narrow this down.
 */
export function ClubFilter({ clubs, selected, onToggle, onSet, counts }: Props) {
  const { t, lang } = useI18n()
  const groups = useMemo(() => {
    const byRegion = new Map<Region, Club[]>()
    for (const club of clubs) {
      const bucket = byRegion.get(club.region)
      if (bucket) bucket.push(club)
      else byRegion.set(club.region, [club])
    }
    return REGION_ORDER.filter((r) => byRegion.has(r)).map(
      (region) => [region, byRegion.get(region)!] as const,
    )
  }, [clubs])

  const allOn = clubs.every((c) => selected.has(c.id))

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onSet(allOn ? [] : clubs.map((c) => c.id))}
          className="rounded-full border px-2.5 py-1 text-xs font-medium transition-colors"
          style={{ borderColor: 'var(--border-strong)', color: 'var(--text-muted)' }}
        >
          {allOn ? t.clearAll : t.selectAll}
        </button>
        <span className="text-xs" style={{ color: 'var(--text-faint)' }}>
          {t.clubCount(selected.size, clubs.length)}
        </span>
      </div>

      {groups.map(([region, regionClubs]) => {
        const regionOn = regionClubs.every((c) => selected.has(c.id))
        const others = clubs.filter((c) => c.region !== region && selected.has(c.id)).map((c) => c.id)

        return (
          <div key={region} className="flex min-w-0 flex-col gap-1">
            <button
              type="button"
              onClick={() =>
                onSet(regionOn ? others : [...others, ...regionClubs.map((c) => c.id)])
              }
              className="self-start text-[10px] font-semibold tracking-wider uppercase transition-colors"
              style={{ color: regionOn ? 'var(--text-muted)' : 'var(--text-faint)' }}
              title={t.region[region]}
            >
              {t.region[region]}
            </button>

            {/* Wrapping, not sideways scrolling: a row that scrolls hides half
                its clubs behind a gesture nobody knows is there, and clips the
                names mid-word. The panel is tall enough to take the wrap. */}
            <div className="flex flex-wrap items-center gap-1.5">
              {regionClubs.map((club) => {
                const on = selected.has(club.id)
                const count = counts.get(club.id) ?? 0
                return (
                  <button
                    key={club.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => onToggle(club.id)}
                    title={[`${club.name} - ${club.municipality}`, club.phone, lang === 'pt' ? (club.notePt ?? club.note) : club.note]
                      .filter(Boolean)
                      .join('\n')}
                    className="flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-all"
                    style={{
                      borderColor: on ? club.color : 'var(--border)',
                      background: on
                        ? `color-mix(in srgb, ${club.color} 14%, transparent)`
                        : 'transparent',
                      color: on ? 'var(--text)' : 'var(--text-faint)',
                    }}
                  >
                    {club.live ? (
                      <span
                        className="size-2 rounded-full transition-opacity"
                        style={{ background: club.color, opacity: on ? 1 : 0.4 }}
                      />
                    ) : (
                      <PhoneIcon dim={!on} />
                    )}
                    {club.shortName}
                    {on && count > 0 && (
                      <span className="nums" style={{ color: 'var(--text-muted)' }}>
                        {count}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })}

      {clubs.some((c) => !c.live) && (
        <p className="mt-0.5 flex items-center gap-1.5 text-[11px]" style={{ color: 'var(--text-faint)' }}>
          <PhoneIcon />
          {t.legend}
        </p>
      )}
    </div>
  )
}

/** Marks a club you can only book by phone. */
function PhoneIcon({ dim = false }: { dim?: boolean }) {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 16 16"
      fill="currentColor"
      className="shrink-0"
      style={{ color: MUTED, opacity: dim ? 0.55 : 1 }}
      aria-hidden
    >
      <path d="M5.2 1.6c.5 0 .9.3 1.1.8l.9 2.2c.2.4.1.9-.2 1.2l-.9.9a9 9 0 0 0 3.2 3.2l.9-.9c.3-.3.8-.4 1.2-.2l2.2.9c.5.2.8.6.8 1.1v2.1c0 .7-.6 1.3-1.3 1.2C6.6 13.6 2.4 9.4 1.7 2.9c-.1-.7.5-1.3 1.2-1.3h2.3Z" />
    </svg>
  )
}
